/**
 * @name SessionProvider
 * Inspired by: https://next-auth.js.org/ but uses cookies from Rails app.
 */

import { Base64 } from "js-base64";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { axiosInstance } from "~/lib/axios-instance";
import { errorStatus, ProblemDetailError } from "~/lib/error";
import { useAsyncError } from "~/lib/hooks/use-async-error";
import { useOnline } from "~/lib/hooks/use-online";
import { now } from "~/lib/utils";
import { User } from "~/models";
import { useAirbrake } from "./airbrake";

let broadcastChannel: BroadcastChannel | null = null;

export const createBroadcastChannel = (channel = "auth") => {
  return new BroadcastChannel(channel);
};

export const broadcast = (channel = "auth") => {
  if (typeof BroadcastChannel === "undefined") {
    return {
      postMessage: () => {},
      addEventListener: () => {},
      removeEventListener: () => {},
    };
  }
  if (broadcastChannel === null)
    broadcastChannel = createBroadcastChannel(channel);
  return broadcastChannel;
};

export interface Session {
  user: User;
}

export type Status = "loading" | "authenticated" | "unauthenticated";

export interface SessionContextValue {
  status: Status;
  session: Session | null;
  error: ProblemDetailError | Error | unknown | null;
}

export type Action =
  | "mount"
  | "routeChange"
  | "focus"
  | "interval"
  | "custom"
  | null;

export interface UseSessionOptions {
  required?: boolean;
  onUnauthenticated?: (action: Action) => void;
}

export interface SessionProviderProps {
  children: React.ReactNode;
  refetchOnRouteChange?: boolean;
  refetchOnWindowFocus?: boolean;
  refetchInterval?: number;
  refetchWhenOffline?: boolean;
}

export interface CheckSessionProps {
  action: Action;
  force?: boolean;
}

export interface SessionInstanceProps {
  checkSession: ({ action, force }: CheckSessionProps) => Promise<void>;
  lastFetchTime: number;
}

export const sessionInstance: SessionInstanceProps = {
  checkSession: async () => {},
  lastFetchTime: 0,
};

/**
 * Logs out via the Insurer Portal API, and then forces the checkSession
 */
export const resetSession = async (): Promise<Session> => {
  const response = await axiosInstance.delete("/api/v0/session");
  await sessionInstance.checkSession({ action: "custom", force: true });
  return response.data;
};

/**
 * Gets session data from Insurer Portal API (if cookie is valid this will return current user)
 */
const getSession = async (): Promise<Session> => {
  const response = await axiosInstance.get("/api/v0/session");
  return response.data;
};

export const SessionContext = React.createContext<SessionContextValue | null>(
  null,
);

export const useSession = (
  options?: UseSessionOptions,
): SessionContextValue => {
  const contextValue = React.useContext<SessionContextValue | null>(
    SessionContext,
  );
  if (!contextValue) {
    throw new Error("`useSession` must be wrapped in an <SessionProvider />");
  }

  const { required, onUnauthenticated } = options ?? {
    required: false,
  };

  const requiredAndUnauthenticated =
    required && contextValue.status === "unauthenticated";

  const error = contextValue.error;
  const throwAsyncError = useAsyncError();

  const checkSessionAction = React.useCallback(
    (event: MessageEvent<{ action: Action }>) => {
      const action = event?.data?.action || null;
      if (errorStatus(error) === 401) {
        if (requiredAndUnauthenticated) {
          const url = `/sessions/login?${new URLSearchParams({
            redirect_url: Base64.encode(
              window.location.pathname + window.location.search,
            ),
          })}`;
          if (onUnauthenticated) onUnauthenticated(action);
          else window.location.href = url;
        }
      } else if (error) throwAsyncError(error as Error); // throw to ErrorBoundary
    },
    [requiredAndUnauthenticated, onUnauthenticated],
  );

  React.useEffect(() => {
    broadcast().addEventListener(
      "message",
      checkSessionAction as EventListenerOrEventListenerObject,
    );
    return () => {
      broadcast().removeEventListener(
        "message",
        checkSessionAction as EventListenerOrEventListenerObject,
      );
    };
  }, [checkSessionAction]);

  return contextValue;
};

export const SessionProvider = ({
  children,
  refetchOnRouteChange = false,
  refetchOnWindowFocus = false,
  refetchWhenOffline = false,
  refetchInterval = 0,
}: SessionProviderProps): React.ReactNode => {
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<any>(null);
  const location = useLocation();
  const { isOnline } = useOnline();
  const shouldRefetch = refetchWhenOffline || isOnline;

  sessionInstance.checkSession = React.useCallback(
    async ({ action, force = false }) => {
      try {
        setError(null);
        const currentTime = now();
        const comparison = refetchInterval ? refetchInterval : 60;
        if (
          !force &&
          sessionInstance.lastFetchTime !== 0 &&
          currentTime - sessionInstance.lastFetchTime < comparison
        ) {
          return;
        }
        sessionInstance.lastFetchTime = currentTime;
        const response = await getSession();
        setSession(response);
      } catch (error) {
        setSession(null);
        setError(error);
      } finally {
        setLoading(false);
        const broadcastChannel = createBroadcastChannel();
        broadcastChannel.postMessage({ action: action });
      }
    },
    [],
  );

  const { notify } = useAirbrake();

  React.useEffect(() => {
    (async () => {
      if (error) {
        const notice = await notify(error);
        return notice;
      }
    })();
  }, [error, notify]);

  // check session on mount
  React.useEffect(() => {
    (async () => {
      await sessionInstance.checkSession({ action: "mount", force: true });
    })();
  }, []);

  // check session on route change
  React.useEffect(() => {
    (async () => {
      if (!refetchOnRouteChange) return;
      if (loading) return;
      await sessionInstance.checkSession({ action: "routeChange" });
    })();
  }, [refetchOnRouteChange, location]);

  // check session on window focus
  React.useEffect(() => {
    const visibilityHandler = async () => {
      if (refetchOnWindowFocus && document.visibilityState === "visible") {
        if (!session) return;
        await sessionInstance.checkSession({ action: "focus" });
      }
    };
    document.addEventListener("visibilitychange", visibilityHandler, false);
    return () => {
      document.removeEventListener(
        "visibilitychange",
        visibilityHandler,
        false,
      );
    };
  }, [refetchOnWindowFocus]);

  // check session every refetchInterval seconds
  React.useEffect(() => {
    if (!refetchInterval && !shouldRefetch) return;
    const refetchIntervalTimer = setInterval(() => {
      (async () => {
        if (!session) return;
        await sessionInstance.checkSession({ action: "interval" });
      })();
    }, refetchInterval * 1000);
    return () => clearInterval(refetchIntervalTimer);
  }, [refetchInterval, shouldRefetch]);

  const defaultContextValue: SessionContextValue = React.useMemo(
    () => ({
      status: loading
        ? "loading"
        : session
          ? "authenticated"
          : "unauthenticated",
      session: session,
      error: error,
    }),
    [session, loading],
  );

  return (
    <SessionContext.Provider value={defaultContextValue}>
      {children}
    </SessionContext.Provider>
  );
};
